import { useEffect, useMemo, useState } from 'react'
import { useToast } from '../lib/toast'
import { api } from '../lib/api'
import { Thumb } from './Thumb'
import { ItemEditor } from './ItemEditor'
import { BulkAdd } from './BulkAdd'
import { Modal } from './Modal'
import { useCollapsedCategories } from '../lib/collapse'
import { type Bootstrap, type Item } from '@shared/types'
import { CHECK_THE_PACKET, conflictLine, conflictMark, conflictOf, type Conflict } from '../lib/conflicts'

interface Props {
  boot: Bootstrap
  onItemsChanged: (items: Item[]) => void
  onError: (err: unknown, fallback: string) => void
  /** Select mode owns the bottom-right corner, where the feedback button is. */
  onPickingChange: (picking: boolean) => void
}

/** Manage the library of foods: add, edit, photograph, archive. */
export function PantryPage({ boot, onItemsChanged, onError, onPickingChange }: Props) {
  /*
   * What a food runs into, from what this family has told us. A food with no
   * tags shows nothing, which means nobody has said -- not that it is fine.
   */
  const clash = useMemo(() => {
    const m = new Map<string, Conflict>()
    for (const i of boot.items) {
      const c = conflictOf(i, boot.allergens)
      if (c) m.set(i.id, c)
    }
    return m
  }, [boot.items, boot.allergens])

  const [query, setQuery] = useState('')
  const { collapsed, toggle } = useCollapsedCategories()
  const [showArchived, setShowArchived] = useState(false)
  /*
   * 'flagged' is the one worth having: "what in here does my vegetarian not
   * eat" is a list you act on, by editing or archiving each row. 'clear' is
   * the other half and is named for what it is -- nothing matched what we
   * were told -- because calling it 'safe' would be a claim the app cannot
   * make. See CHECK_THE_PACKET.
   */
  const [only, setOnly] = useState<'all' | 'flagged' | 'clear'>('all')
  const [editing, setEditing] = useState<{ item: Item | null; categoryId: string } | null>(null)
  const [bulk, setBulk] = useState(false)
  /*
   * Select mode. Null when off, so a tap on a card opens the editor as it
   * always has -- the two meanings of a tap never overlap.
   */
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const [working, setWorking] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [kept, setKept] = useState<{ id: string; name: string; usedCount: number }[]>([])
  const toast = useToast()

  useEffect(() => onPickingChange(picked !== null), [picked, onPickingChange])
  useEffect(() => () => onPickingChange(false), [onPickingChange])

  async function reload() {
    try {
      const fresh = await api.bootstrap()
      onItemsChanged(fresh.items)
    } catch (err) {
      onError(err, 'Could not refresh the pantry.')
    }
  }

  /** Every id currently on screen, so "Select all" means what is shown. */
  const shownIds = () => grouped.flatMap(({ items }) => items.map((i) => i.id))

  async function run(action: 'delete' | 'archive' | 'unarchive') {
    const ids = [...(picked ?? [])]
    if (ids.length === 0 || working) return
    setWorking(true)
    try {
      const r = await api.batchItems(ids, action)
      /*
       * The ones a planned week still needs stay selected and stay named, so
       * the next tap can be Archive rather than a hunt through the pantry for
       * which four of the twelve did not go.
       */
      setKept(r.kept)
      /*
       * Stays in select mode with the selection cleared, rather than closing
       * it. Pruning is rarely one batch, and dropping out after each one puts
       * the button back between you and the next.
       */
      setPicked(new Set(r.kept.map((k) => k.id)))
      const n = r.changed.length
      const what = n === 1 ? '1 food' : `${n} foods`
      if (action === 'delete') {
        toast.notify(
          n === 0
            ? 'Nothing removed.'
            : `Removed ${what}.${r.removedFromMeals > 0 ? ` ${r.removedFromMeals} saved meal${r.removedFromMeals === 1 ? '' : 's'} lost a component.` : ''}`,
        )
      } else {
        toast.notify(action === 'archive' ? `Archived ${what}.` : `${what} back in the pantry.`)
      }
      await reload()
    } catch (err) {
      onError(err, 'Could not change those items.')
    } finally {
      setWorking(false)
      setConfirmDelete(false)
    }
  }

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return boot.categories.map((category) => ({
      category,
      items: boot.items.filter(
        (i) =>
          i.categoryId === category.id &&
          (showArchived || !i.archived) &&
          (only === 'all' || (only === 'flagged') === clash.has(i.id)) &&
          (needle === '' ||
            i.name.toLowerCase().includes(needle) ||
            i.notes.toLowerCase().includes(needle)),
      ),
    }))
  }, [boot, query, showArchived, only, clash])

  const searching = query.trim().length > 0
  const archivedCount = boot.items.filter((i) => i.archived).length
  /*
   * Offered only once the family has told us something to check against.
   * With nothing set every food is unflagged and the filter is three buttons
   * that do nothing.
   */
  const flaggedCount = boot.items.filter((i) => !i.archived && clash.has(i.id)).length
  const canFilter = boot.allergens.length > 0

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pantry</h1>
          <p>
            {picked
              ? 'Tap the foods you want to archive or delete.'
              : `Everything ${boot.profileName} will actually eat, by category. Tap an item to edit it.`}
          </p>
        </div>
        <div className="spacer" />
        {/*
          Tidying is the other half of adding, and until now it was one food
          per dialog: open, archive, close, find the next one. The pantry a
          family wants to prune is exactly the one long enough to make that
          not worth starting.
        */}
        <button
          className={`btn ${picked ? 'primary' : ''}`}
          aria-pressed={picked !== null}
          onClick={() => {
            setPicked(picked ? null : new Set())
            setKept([])
          }}
        >
          {picked ? 'Done' : 'Select'}
        </button>
        <button className="btn" onClick={() => setBulk(true)} disabled={picked !== null}>
          {boot.aiAvailable ? '✨ ' : ''}Bulk add
        </button>
        <button
          className="btn primary"
          onClick={() => setEditing({ item: null, categoryId: boot.categories[0]!.id })}
          disabled={picked !== null}
        >
          + Add item
        </button>
      </div>

      <div className="row wrap" style={{ marginBottom: 16 }}>
        <input
          className="pantry-search"
          style={{ maxWidth: 300, marginBottom: 0 }}
          type="search"
          autoComplete="off"
          placeholder="Search the pantry…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the pantry"
        />
        {archivedCount > 0 && (
          <button className="btn sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
          </button>
        )}
      </div>

      {/*
        A pantry is long and a restriction is new, so the question after
        setting one is "what in here does that rule out?". Reading every card
        for a badge is the slow way to answer it.
      */}
      {canFilter && (
        <div className="pantry-filter" role="group" aria-label="Filter by what it runs into">
          {(
            [
              ['all', 'All foods'],
              ['flagged', `Flagged${flaggedCount > 0 ? ` (${flaggedCount})` : ''}`],
              ['clear', 'Nothing flagged'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`pantry-filter-tab ${only === value ? 'on' : ''}`}
              aria-pressed={only === value}
              onClick={() => setOnly(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {only !== 'all' && (
        <p className="muted small pantry-filter-note">
          {only === 'flagged'
            ? `Foods that run into something you have told us about ${boot.profileName}. Tap one to edit its tags, or archive it.`
            : 'Foods that ran into nothing we were told about — which is not the same as nothing being in them.'}{' '}
          {CHECK_THE_PACKET}
        </p>
      )}

      {/*
        A filtered pantry drops the categories it emptied. "Nothing here yet"
        under every heading is true of the filter, not of the pantry, and a
        page of it buries the handful of rows actually being looked for.
      */}
      {grouped
        .filter(({ items }) => only === 'all' || items.length > 0)
        .map(({ category, items }) => (
        <div className="cat-group" key={category.id}>
          <button
            type="button"
            className="cat-head"
            aria-expanded={searching || !collapsed.has(category.id)}
            onClick={() => toggle(category.id)}
          >
            <span className="cat-chevron" aria-hidden="true">
              {searching || !collapsed.has(category.id) ? '▾' : '▸'}
            </span>
            <span className="slot-dot" style={{ background: category.color }} aria-hidden="true" />
            {category.emoji} {category.name}
            {category.blurb && (
              <span className="muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                — {category.blurb}
              </span>
            )}
            <span className="count">{items.length}</span>
          </button>

          {!(searching || !collapsed.has(category.id)) ? null : items.length === 0 ? (
            <p className="muted" style={{ fontSize: 13.5, margin: '0 0 8px' }}>
              Nothing here yet.
            </p>
          ) : (
            <div className="pantry-grid">
              {items.map((item) => (
                <button
                  key={item.id}
                  className={`pantry-card ${item.archived ? 'archived' : ''} ${
                    picked?.has(item.id) ? 'picked' : ''
                  }`}
                  aria-pressed={picked ? picked.has(item.id) : undefined}
                  onClick={() =>
                    picked
                      ? setPicked((prev) => {
                          const next = new Set(prev)
                          if (!next.delete(item.id)) next.add(item.id)
                          return next
                        })
                      : setEditing({ item, categoryId: item.categoryId })
                  }
                >
                  {picked && (
                    <span className="pantry-tick" aria-hidden="true">
                      {picked.has(item.id) ? '✓' : ''}
                    </span>
                  )}
                  <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} size="lg" />
                  <span className="body">
                    <span className="title">{item.name}</span>
                    {(item.archived || item.notes) && (
                      <span className="sub">{item.archived ? 'Archived' : item.notes}</span>
                    )}
                    {/*
                      A badge, not a second note. It used to render in the
                      same grey line as "Sunbutter or PB", listing what
                      matched -- so a turkey sandwich under a vegetarian rule
                      read "Vegetarian", as though that is what it is. It now
                      says what it means, in the wording the shopping list and
                      the board already use.
                    */}
                    {clash.has(item.id) && (
                      <span className={`pantry-clash ${clash.get(item.id)!.level}`}>
                        <span className="pantry-clash-mark" aria-hidden="true">
                          {conflictMark(clash.get(item.id)!)}
                        </span>
                        {conflictLine(clash.get(item.id)!, boot.profileName)}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}

          {only === 'all' && (searching || !collapsed.has(category.id)) && (
            <button
              className="btn sm ghost"
              onClick={() => setEditing({ item: null, categoryId: category.id })}
            >
              + Add {category.name.toLowerCase()}
            </button>
          )}
        </div>
        ))}

      {only !== 'all' && grouped.every(({ items }) => items.length === 0) && (
        <p className="muted">
          {only === 'flagged'
            ? 'Nothing in the pantry runs into anything you have told us about.'
            : 'Every food in the pantry runs into something.'}
        </p>
      )}

      {/*
        Pinned rather than at the end of the page, for the same reason the
        allergen Save is: the list you are working through is longer than the
        screen, and the action has to stay under your thumb.
      */}
      {picked && (
        <div className="pantry-bar" role="status">
          <span className="pantry-bar-what">
            {picked.size === 0
              ? 'Tap the foods you want to change.'
              : `${picked.size} chosen`}
          </span>
          <button
            className="btn sm"
            disabled={working}
            onClick={() => {
              const shown = shownIds()
              setPicked((prev) =>
                prev && shown.every((id) => prev.has(id)) ? new Set() : new Set(shown),
              )
            }}
          >
            {shownIds().length > 0 && shownIds().every((id) => picked.has(id)) ? 'None' : 'All'}
          </button>
          <button className="btn sm" disabled={working || picked.size === 0} onClick={() => run('archive')}>
            Archive
          </button>
          <button
            className="btn sm danger"
            disabled={working || picked.size === 0}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </button>
        </div>
      )}

      {/*
        Named, and counted. Deleting a dozen foods at once is the one place in
        this app where a slip is expensive and silent, so it says what and how
        many before it happens rather than offering an undo it does not have.
      */}
      {confirmDelete && picked && (
        <Modal
          title={`Delete ${picked.size === 1 ? 'this food' : `these ${picked.size} foods`}?`}
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button className="btn ghost" onClick={() => setConfirmDelete(false)} disabled={working}>
                Cancel
              </button>
              <div className="spacer" />
              <button className="btn danger" onClick={() => run('delete')} disabled={working}>
                {working ? 'Deleting…' : `Delete ${picked.size}`}
              </button>
            </>
          }
        >
          <p>
            Gone from the pantry for good, along with their photos. Anything a planned week still
            uses is kept instead, and named afterwards, so your history stays intact.
          </p>
        </Modal>
      )}

      {kept.length > 0 && (
        <Modal
          title={kept.length === 1 ? 'One is still in a planned lunch' : 'Some are still in a planned lunch'}
          onClose={() => setKept([])}
          footer={
            <>
              <button className="btn ghost" onClick={() => setKept([])} disabled={working}>
                Leave them
              </button>
              <div className="spacer" />
              <button
                className="btn primary"
                disabled={working}
                onClick={() => {
                  setKept([])
                  void run('archive')
                }}
              >
                Archive {kept.length === 1 ? 'it' : `all ${kept.length}`}
              </button>
            </>
          }
        >
          <p>
            Deleting these would take a card out of a week you have already planned, so they were
            kept. Archiving does the same job to the pantry and leaves your history alone.
          </p>
          <ul className="kept-list">
            {kept.map((k) => (
              <li key={k.id}>
                <b>{k.name}</b> — in {k.usedCount} planned lunch{k.usedCount === 1 ? '' : 'es'}
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {bulk && (
        <BulkAdd
          categories={boot.categories}
          defaultCategoryId={boot.categories[0]!.id}
          aiAvailable={boot.aiAvailable}
          aiNote={
            boot.aiConfigured && !boot.aiAvailable ? 'The assistant sorts them for you once your family is approved.' : ''
          }
          onAdded={reload}
          onClose={() => setBulk(false)}
          onError={onError}
        />
      )}

      {editing && (
        <ItemEditor
          categories={boot.categories}
          item={editing.item}
          defaultCategoryId={editing.categoryId}
          watchFor={boot.allergens}
          onSaved={reload}
          onClose={() => setEditing(null)}
          onError={onError}
        />
      )}
    </>
  )
}
